import { useState } from 'react'
import { useSession } from '@/hooks/useSession'
import { useToast } from '@/components/ui/Toast'
import { Spinner } from '@/components/ui/Spinner'
import {
  seedBracket, finalizeR32, triggerSync, getSyncStatus, importAiPlayer,
  getPlayers, removePlayer, getAdminSettings, updateAdminSettings,
  recomputeScores, createInviteCode, getBracketTemplate, setMatchResult,
  assignTeams, correctMatch, getAdminTeams, getStandings, getAdminGroups, createAdminGroup,
  renameAdminGroup, deleteAdminGroup, getGroupPlayers, removeGroupPlayer,
} from '@/api/admin'
import type { StandingsGroup, ProposedAssignment, AdminGroup, GroupPlayer } from '@/api/admin'
import { fetchMatches } from '@/api/bracket'
import type { Match, Team } from '@/types/bracket'

type Tab = 'sync' | 'teams' | 'results' | 'ai' | 'players' | 'groups' | 'settings'

export function AdminDashboard() {
  const session = useSession()
  const { toast } = useToast()
  const [tab, setTab] = useState<Tab>('sync')
  const [secret, setSecret] = useState(() => localStorage.getItem('admin_secret') ?? '')
  const [loading, setLoading] = useState(false)

  // Admin access is gated by ADMIN_SECRET on the backend — no frontend redirect needed

  function saveSecret(v: string) {
    setSecret(v)
    localStorage.setItem('admin_secret', v)
  }

  async function run<T>(fn: () => Promise<T>, successMsg?: string): Promise<T | undefined> {
    setLoading(true)
    try {
      const result = await fn()
      if (successMsg) toast(successMsg, 'success')
      return result
    } catch (err: unknown) {
      toast(err instanceof Error ? err.message : 'Error', 'error')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen">
      <div className="border-b border-white/10 bg-pitch-900/80 backdrop-blur-sm sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-xl">⚙️</span>
            <h1 className="text-base font-black text-white">Admin Panel</h1>
          </div>
          <nav className="flex gap-2">
            <a href="/bracket" className="btn-secondary btn-sm text-sm">⚽ Bracket</a>
            <a href="/leaderboard" className="btn-secondary btn-sm text-sm">🏅 Leaderboard</a>
          </nav>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 flex flex-col gap-6">
        {/* Admin secret input */}
        <div className="card p-4">
          <label className="text-xs text-white/50 mb-2 block">Admin secret (X-Admin-Secret header)</label>
          <input
            type="password"
            className="input"
            value={secret}
            onChange={e => saveSecret(e.target.value)}
            placeholder="Enter admin secret…"
          />
        </div>

        {/* Tab nav */}
        <div className="flex gap-2 flex-wrap">
          {([
            ['sync', '🔄 Sync'],
            ['teams', '🏳 Assign Teams'],
            ['results', '📝 Results'],
            ['ai', '🤖 AI Import'],
            ['players', '👥 Players'],
            ['groups', '🏢 Groups'],
            ['settings', '⚙️ Settings'],
          ] as const).map(([id, label]) => (
            <button
              key={id}
              onClick={() => setTab(id)}
              className={`btn btn-sm ${tab === id ? 'btn-primary' : 'btn-secondary'}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === 'sync' && <SyncTab secret={secret} loading={loading} run={run} />}
        {tab === 'teams' && <AssignTeamsTab secret={secret} loading={loading} run={run} />}
        {tab === 'results' && <ResultsTab secret={secret} loading={loading} run={run} />}
        {tab === 'ai' && <AiTab secret={secret} loading={loading} run={run} toast={toast} />}
        {tab === 'players' && <PlayersTab secret={secret} loading={loading} run={run} />}
        {tab === 'groups' && <GroupsTab secret={secret} loading={loading} run={run} />}
        {tab === 'settings' && <SettingsTab secret={secret} loading={loading} run={run} />}
      </div>
    </div>
  )
}

function SyncTab({ secret, loading, run }: TabProps) {
  const [status, setStatus] = useState<{ lastSyncAt: number | null } | null>(null)

  async function handleStatus() {
    const s = await run(() => getSyncStatus(secret))
    if (s) setStatus(s)
  }

  return (
    <div className="card p-5 flex flex-col gap-4">
      <h2 className="font-bold text-white">Football Data Sync</h2>

      <div className="flex gap-3 flex-wrap">
        <button className="btn-primary" disabled={loading} onClick={() => run(() => seedBracket(secret), 'Bracket seeded (63 matches created)')}>
          {loading ? <Spinner size="sm" /> : null} Seed Bracket
        </button>
        <button
          className="btn-primary bg-emerald-600 hover:bg-emerald-500"
          disabled={loading}
          onClick={() => {
            if (!confirm('Finalize all 16 R32 matchups with actual FIFA 2026 teams? This will clear any existing R32 predictions.')) return
            run(() => finalizeR32(secret), '✅ R32 bracket finalized with all 16 matchups!')
          }}
        >
          {loading ? <Spinner size="sm" /> : null} 🏆 Finalize R32 Bracket
        </button>
        <button className="btn-secondary" disabled={loading} onClick={() => run(() => triggerSync(secret), 'Sync complete')}>
          {loading ? <Spinner size="sm" /> : null} Sync football-data.org
        </button>
        <button className="btn-secondary" disabled={loading} onClick={handleStatus}>
          Check status
        </button>
      </div>

      {status && (
        <div className="bg-white/5 rounded-lg p-3 text-sm text-white/70">
          Last sync: {status.lastSyncAt ? new Date(status.lastSyncAt * 1000).toLocaleString() : 'Never'}
        </div>
      )}

      <p className="text-xs text-white/40">
        Auto-sync runs every 5 minutes via cron when FOOTBALL_DATA_API_KEY is set.
        "Seed Bracket" creates the 63 empty match slots — run once before the tournament.
      </p>
    </div>
  )
}

function ResultsTab({ secret, loading, run }: TabProps) {
  const { toast } = useToast()
  const [matchId, setMatchId] = useState('')
  const [homeScore, setHomeScore] = useState('')
  const [awayScore, setAwayScore] = useState('')
  const [winnerId, setWinnerId] = useState('')

  // Correct a finished match (fix teams + record result in one operation)
  const [cMatchId, setCMatchId] = useState('')
  const [cHome, setCHome] = useState('')
  const [cAway, setCAway] = useState('')
  const [cHomeScore, setCHomeScore] = useState('')
  const [cAwayScore, setCAwayScore] = useState('')
  const [cWinner, setCWinner] = useState('')

  async function handleOverride() {
    if (!matchId || !winnerId) return
    await run(
      () => setMatchResult(matchId, Number(homeScore), Number(awayScore), winnerId, secret),
      'Result saved'
    )
  }

  async function handleCorrect() {
    if (!cMatchId || !cHome || !cAway || !cWinner) return
    const result = await run(
      () => correctMatch(cMatchId, cHome.toUpperCase(), cAway.toUpperCase(), Number(cHomeScore) || 0, Number(cAwayScore) || 0, cWinner.toUpperCase(), secret),
    )
    if (result) {
      const preserved = result.predictionsPreservedFor.join(', ')
      const removed = result.removedTeams.length
        ? `Cleared predictions for ${result.removedTeams.join(', ')}.`
        : 'No predictions lost.'
      toast(`Done! ${removed} Preserved predictions for: ${preserved}`, 'success')
      setCMatchId(''); setCHome(''); setCAway(''); setCHomeScore(''); setCAwayScore(''); setCWinner('')
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Primary fix: correct teams + result in one step */}
      <div className="card p-5 flex flex-col gap-4 border border-amber-500/30 bg-amber-900/10">
        <div>
          <h2 className="font-bold text-white">Correct Finished Match</h2>
          <p className="text-xs text-white/50 mt-1">
            Use when a match had wrong teams due to bracket seeding errors.
            Surgically preserves predictions for teams that remain in the match — only clears picks for swapped-out teams.
            Also fixes the next round's team slot.
          </p>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2">
            <label className="text-xs text-white/50 mb-1 block">Match ID</label>
            <input className="input" value={cMatchId} onChange={e => setCMatchId(e.target.value)} placeholder="m_sf_01" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Correct home team ID</label>
            <input className="input" value={cHome} onChange={e => setCHome(e.target.value)} placeholder="FRA" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Correct away team ID</label>
            <input className="input" value={cAway} onChange={e => setCAway(e.target.value)} placeholder="ESP" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Home score</label>
            <input type="number" className="input" value={cHomeScore} onChange={e => setCHomeScore(e.target.value)} placeholder="1" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Away score</label>
            <input type="number" className="input" value={cAwayScore} onChange={e => setCAwayScore(e.target.value)} placeholder="2" />
          </div>
          <div className="col-span-2">
            <label className="text-xs text-white/50 mb-1 block">Winner team ID</label>
            <input className="input" value={cWinner} onChange={e => setCWinner(e.target.value)} placeholder="ESP" />
          </div>
        </div>
        <button
          className="btn-primary bg-amber-600 hover:bg-amber-500"
          disabled={loading || !cMatchId || !cHome || !cAway || !cWinner}
          onClick={handleCorrect}
        >
          {loading ? <Spinner size="sm" /> : '🔧'} Correct Match
        </button>
        <div className="bg-white/5 rounded-lg p-3 text-xs text-white/50 font-mono leading-relaxed">
          <div className="text-white/30 mb-1">Example — fix both SFs and propagate correct Final:</div>
          <div>SF #1 → m_sf_01 · FRA vs ESP · Spain wins → 1 2 ESP</div>
          <div>SF #2 → m_sf_02 · ENG vs ARG · Argentina wins → 1 2 ARG</div>
        </div>
      </div>

      <div className="card p-5 flex flex-col gap-4">
        <h2 className="font-bold text-white">Manual Result Override</h2>
        <p className="text-xs text-white/40">For matches with correct teams — just records scores and propagates winner.</p>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs text-white/50 mb-1 block">Match ID</label>
            <input className="input" value={matchId} onChange={e => setMatchId(e.target.value)} placeholder="m_r32_01" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Winner team ID</label>
            <input className="input" value={winnerId} onChange={e => setWinnerId(e.target.value)} placeholder="ARG" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Home score</label>
            <input type="number" className="input" value={homeScore} onChange={e => setHomeScore(e.target.value)} placeholder="2" />
          </div>
          <div>
            <label className="text-xs text-white/50 mb-1 block">Away score</label>
            <input type="number" className="input" value={awayScore} onChange={e => setAwayScore(e.target.value)} placeholder="1" />
          </div>
        </div>
        <button className="btn-primary" disabled={loading || !matchId || !winnerId} onClick={handleOverride}>
          {loading ? <Spinner size="sm" /> : null} Set Result
        </button>
      </div>
    </div>
  )
}

function AiTab({ secret, loading, run, toast }: TabProps & { toast: (m: string, t?: 'success' | 'error' | 'info') => void }) {
  const [aiName, setAiName] = useState('')
  const [jsonText, setJsonText] = useState('')

  async function handleExportTemplate() {
    const tpl = await run(() => getBracketTemplate(secret))
    if (tpl) {
      const blob = new Blob([JSON.stringify(tpl, null, 2)], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'wc2026_bracket_template.json'
      a.click()
      URL.revokeObjectURL(url)
      toast('Template downloaded', 'success')
    }
  }

  async function handleImport() {
    if (!aiName.trim() || !jsonText.trim()) return
    try {
      const parsed = JSON.parse(jsonText)
      const preds = parsed.rounds?.flatMap((r: { matches: unknown[] }) => r.matches) ?? []
      await run(() => importAiPlayer(aiName.trim(), preds, secret), `AI player "${aiName}" imported`)
      setAiName('')
      setJsonText('')
    } catch {
      toast('Invalid JSON', 'error')
    }
  }

  return (
    <div className="card p-5 flex flex-col gap-4">
      <h2 className="font-bold text-white">AI Player Predictions</h2>

      <div>
        <p className="text-sm text-white/70 mb-3">
          1. Export the blank template → fill it with ChatGPT or Claude → import it here.
        </p>
        <button className="btn-secondary" disabled={loading} onClick={handleExportTemplate}>
          {loading ? <Spinner size="sm" /> : null} Export Blank Template JSON
        </button>
      </div>

      <div className="border-t border-white/10 pt-4 flex flex-col gap-3">
        <div>
          <label className="text-xs text-white/50 mb-1 block">AI player name</label>
          <input className="input" value={aiName} onChange={e => setAiName(e.target.value)} placeholder="Claude 3.7 Sonnet" />
        </div>
        <div>
          <label className="text-xs text-white/50 mb-1 block">Filled JSON (paste here)</label>
          <textarea
            className="input font-mono text-xs"
            rows={8}
            value={jsonText}
            onChange={e => setJsonText(e.target.value)}
            placeholder='{"schema_version":"1.0","rounds":[...]}'
          />
        </div>
        <button className="btn-primary" disabled={loading || !aiName.trim() || !jsonText.trim()} onClick={handleImport}>
          {loading ? <Spinner size="sm" /> : null} Import AI Player
        </button>
      </div>
    </div>
  )
}

function PlayersTab({ secret, loading, run }: TabProps) {
  const [players, setPlayers] = useState<Array<{ id: string; display_name: string; is_ai: number; created_at: number }>>([])
  const [loaded, setLoaded] = useState(false)

  async function loadPlayers() {
    const p = await run(() => getPlayers(secret) as Promise<Array<{ id: string; display_name: string; is_ai: number; created_at: number }>>)
    if (p) { setPlayers(p); setLoaded(true) }
  }

  async function handleDelete(id: string, name: string) {
    if (!confirm(`Remove player "${name}"? This deletes all their predictions and scores.`)) return
    await run(() => removePlayer(id, secret), `Removed ${name}`)
    setPlayers(prev => prev.filter(p => p.id !== id))
  }

  return (
    <div className="card p-5 flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-white">Players ({players.length})</h2>
        <button className="btn-secondary btn-sm" disabled={loading} onClick={loadPlayers}>
          {loading ? <Spinner size="sm" /> : null} Load
        </button>
      </div>

      {loaded && players.length === 0 && <p className="text-white/40 text-sm">No players yet.</p>}

      <div className="flex flex-col gap-2">
        {players.map(p => (
          <div key={p.id} className="flex items-center justify-between bg-white/5 rounded-lg px-3 py-2">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-white">{p.display_name}</span>
              {p.is_ai === 1 && <span className="badge-ai">🤖 AI</span>}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-white/30">{p.id}</span>
              <button
                className="btn-danger btn-sm text-xs"
                onClick={() => handleDelete(p.id, p.display_name)}
              >
                Remove
              </button>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function SettingsTab({ secret, loading, run }: TabProps) {
  const [settings, setSettings] = useState<Record<string, string>>({})
  const [loaded, setLoaded] = useState(false)
  const [lockInput, setLockInput] = useState('')
  const [inviteCode, setInviteCode] = useState('')

  async function loadSettings() {
    const s = await run(() => getAdminSettings(secret))
    if (s) { setSettings(s); setLoaded(true) }
  }

  async function handleSetLock() {
    const ts = lockInput ? Math.floor(new Date(lockInput).getTime() / 1000) : 0
    await run(() => updateAdminSettings({ prediction_lock_at: String(ts) }, secret), 'Lock time updated')
  }

  async function handleCreateCode() {
    if (!inviteCode.trim()) return
    await run(() => createInviteCode(inviteCode.trim(), null, secret), `Code "${inviteCode}" created`)
    setInviteCode('')
  }

  async function handleRecompute() {
    const res = await run(() => recomputeScores(secret))
    if (res) alert(`Recomputed scores for ${res.matchesScored} matches`)
  }

  return (
    <div className="card p-5 flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h2 className="font-bold text-white">Settings</h2>
        <button className="btn-secondary btn-sm" disabled={loading} onClick={loadSettings}>Load</button>
      </div>

      {loaded && (
        <div className="bg-white/5 rounded-lg p-3 text-xs text-white/50 font-mono">
          {Object.entries(settings).map(([k, v]) => (
            <div key={k}><span className="text-emerald-400">{k}</span> = {v}</div>
          ))}
        </div>
      )}

      <div>
        <label className="text-xs text-white/50 mb-2 block">Prediction lock datetime (local time)</label>
        <div className="flex gap-2">
          <input type="datetime-local" className="input flex-1" value={lockInput} onChange={e => setLockInput(e.target.value)} />
          <button className="btn-primary" disabled={loading} onClick={handleSetLock}>Set Lock</button>
        </div>
        <p className="text-xs text-white/30 mt-1">Set to just before the first R32 match starts</p>
      </div>

      <div>
        <label className="text-xs text-white/50 mb-2 block">Create invite code</label>
        <div className="flex gap-2">
          <input className="input flex-1" value={inviteCode} onChange={e => setInviteCode(e.target.value)} placeholder="my-invite-code" />
          <button className="btn-primary" disabled={loading || !inviteCode.trim()} onClick={handleCreateCode}>Create</button>
        </div>
      </div>

      <div className="border-t border-white/10 pt-4">
        <button className="btn-secondary" disabled={loading} onClick={handleRecompute}>
          {loading ? <Spinner size="sm" /> : null} Force Recompute All Scores
        </button>
        <p className="text-xs text-white/30 mt-1">Use after correcting match results</p>
      </div>
    </div>
  )
}

interface TabProps {
  secret: string
  loading: boolean
  run: <T>(fn: () => Promise<T>, msg?: string) => Promise<T | undefined>
}

function GroupsTab({ secret, loading, run }: TabProps) {
  const { toast } = useToast()
  const [groups, setGroups] = useState<AdminGroup[]>([])
  const [newGroupName, setNewGroupName] = useState('')
  const [newCode, setNewCode] = useState('')
  const [newCodeGroupId, setNewCodeGroupId] = useState('')
  const [newCodeMax, setNewCodeMax] = useState('')
  // Per-group state
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editName, setEditName] = useState('')
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [groupPlayers, setGroupPlayers] = useState<Record<string, GroupPlayer[]>>({})
  const [playersLoading, setPlayersLoading] = useState(false)

  async function loadGroups() {
    const data = await run(() => getAdminGroups(secret))
    if (data) setGroups(data)
  }

  useState(() => { loadGroups() })

  async function handleCreateGroup(e: React.FormEvent) {
    e.preventDefault()
    if (!newGroupName.trim()) return
    const result = await run(() => createAdminGroup(newGroupName.trim(), secret), `Group "${newGroupName.trim()}" created`)
    if (result) { setNewGroupName(''); await loadGroups() }
  }

  async function handleCreateCode(e: React.FormEvent) {
    e.preventDefault()
    if (!newCode.trim()) return
    const max = newCodeMax ? parseInt(newCodeMax) : null
    await run(() => createInviteCode(newCode.trim(), max, secret, newCodeGroupId || undefined), `Code "${newCode.trim()}" created`)
    setNewCode(''); setNewCodeMax('')
    await loadGroups()
  }

  async function handleRename(id: string) {
    if (!editName.trim()) return
    await run(() => renameAdminGroup(id, editName.trim(), secret), 'Group renamed')
    setEditingId(null)
    await loadGroups()
  }

  async function handleDelete(g: AdminGroup) {
    if (!confirm(`Delete group "${g.name}"? Its ${g.player_count} player(s) will become ungrouped.`)) return
    await run(() => deleteAdminGroup(g.id, secret), `Group "${g.name}" deleted`)
    setExpandedId(null)
    await loadGroups()
  }

  async function togglePlayers(groupId: string) {
    if (expandedId === groupId) { setExpandedId(null); return }
    setExpandedId(groupId)
    if (!groupPlayers[groupId]) {
      setPlayersLoading(true)
      try {
        const players = await getGroupPlayers(groupId, secret)
        setGroupPlayers(prev => ({ ...prev, [groupId]: players }))
      } catch { toast('Failed to load players', 'error') }
      finally { setPlayersLoading(false) }
    }
  }

  async function handleRemovePlayer(groupId: string, player: GroupPlayer) {
    if (!confirm(`Remove "${player.display_name}" and all their predictions?`)) return
    await run(() => removeGroupPlayer(groupId, player.id, secret), `Removed ${player.display_name}`)
    setGroupPlayers(prev => ({
      ...prev,
      [groupId]: (prev[groupId] ?? []).filter(p => p.id !== player.id),
    }))
    setGroups(prev => prev.map(g => g.id === groupId ? { ...g, player_count: g.player_count - 1 } : g))
  }

  return (
    <div className="flex flex-col gap-6">
      {/* Groups list */}
      <div className="card p-4">
        <h3 className="text-sm font-bold text-white mb-3">Groups</h3>
        {groups.length === 0 ? (
          <p className="text-sm text-white/40">No groups yet.</p>
        ) : (
          <div className="flex flex-col gap-3">
            {groups.map(g => (
              <div key={g.id} className="bg-white/5 rounded-lg overflow-hidden">
                <div className="flex items-center gap-2 p-3">
                  {editingId === g.id ? (
                    <>
                      <input className="input flex-1 h-8 text-sm py-1"
                        value={editName} onChange={e => setEditName(e.target.value)}
                        onKeyDown={e => { if (e.key === 'Enter') handleRename(g.id); if (e.key === 'Escape') setEditingId(null) }}
                        autoFocus />
                      <button className="btn-primary btn-sm text-xs" onClick={() => handleRename(g.id)} disabled={loading}>Save</button>
                      <button className="btn-secondary btn-sm text-xs" onClick={() => setEditingId(null)}>Cancel</button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 min-w-0">
                        <span className="font-semibold text-white">{g.name}</span>
                        <span className="ml-2 text-xs text-white/30">{g.id}</span>
                      </div>
                      <button
                        onClick={() => togglePlayers(g.id)}
                        className="text-xs text-white/50 hover:text-white/80 transition-colors px-2 py-1 rounded hover:bg-white/5"
                      >
                        {g.player_count} player{g.player_count !== 1 ? 's' : ''} {expandedId === g.id ? '▲' : '▼'}
                      </button>
                      <button
                        onClick={() => { setEditingId(g.id); setEditName(g.name) }}
                        className="text-xs text-white/40 hover:text-white/80 transition-colors px-2 py-1 rounded hover:bg-white/5"
                        title="Rename"
                      >✏️</button>
                      <button
                        onClick={() => handleDelete(g)}
                        className="text-xs text-red-400/60 hover:text-red-400 transition-colors px-2 py-1 rounded hover:bg-red-500/10"
                        title="Delete group"
                      >🗑</button>
                    </>
                  )}
                </div>

                {/* Invite codes */}
                {g.invite_codes.length > 0 && (
                  <div className="px-3 pb-2 flex flex-wrap gap-1.5">
                    {g.invite_codes.map(ic => (
                      <span key={ic.code} className="text-xs bg-emerald-500/10 border border-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full">
                        {ic.code} ({ic.use_count}{ic.max_uses ? `/${ic.max_uses}` : ''} uses)
                      </span>
                    ))}
                  </div>
                )}

                {/* Player list (expandable) */}
                {expandedId === g.id && (
                  <div className="border-t border-white/10 p-3">
                    {playersLoading && !groupPlayers[g.id] ? (
                      <div className="flex justify-center py-4"><Spinner size="sm" /></div>
                    ) : (groupPlayers[g.id] ?? []).length === 0 ? (
                      <p className="text-xs text-white/30 text-center py-2">No players yet.</p>
                    ) : (
                      <div className="flex flex-col gap-1.5">
                        {(groupPlayers[g.id] ?? []).map(p => (
                          <div key={p.id} className="flex items-center justify-between bg-white/5 rounded px-2.5 py-1.5">
                            <div className="flex items-center gap-2 min-w-0">
                              {p.is_ai === 1 && <span className="text-xs">🤖</span>}
                              <span className="text-sm text-white truncate">{p.display_name}</span>
                              {p.email && <span className="text-[10px] text-white/30 truncate hidden sm:block">{p.email}</span>}
                            </div>
                            <button
                              onClick={() => handleRemovePlayer(g.id, p)}
                              className="text-xs text-red-400/60 hover:text-red-400 transition-colors px-1.5 py-0.5 rounded hover:bg-red-500/10 shrink-0 ml-2"
                            >
                              Remove
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create group */}
      <div className="card p-4">
        <h3 className="text-sm font-bold text-white mb-3">Create Group</h3>
        <form onSubmit={handleCreateGroup} className="flex gap-2">
          <input className="input flex-1" placeholder="Group name…"
            value={newGroupName} onChange={e => setNewGroupName(e.target.value)} />
          <button type="submit" className="btn-primary btn-sm" disabled={loading}>Create</button>
        </form>
      </div>

      {/* Create invite code */}
      <div className="card p-4">
        <h3 className="text-sm font-bold text-white mb-3">Add Invite Code</h3>
        <form onSubmit={handleCreateCode} className="flex flex-col gap-3">
          <div className="flex gap-2">
            <input className="input flex-1" placeholder="Code (e.g. gospark)"
              value={newCode} onChange={e => setNewCode(e.target.value)} />
            <input className="input w-24" placeholder="Max uses" type="number" min={1}
              value={newCodeMax} onChange={e => setNewCodeMax(e.target.value)} />
          </div>
          <div className="flex gap-2">
            <select className="input flex-1" value={newCodeGroupId} onChange={e => setNewCodeGroupId(e.target.value)}>
              <option value="">— No group —</option>
              {groups.map(g => <option key={g.id} value={g.id}>{g.name}</option>)}
            </select>
            <button type="submit" className="btn-primary btn-sm" disabled={loading}>Add Code</button>
          </div>
        </form>
      </div>
    </div>
  )
}

function AssignTeamsTab({ secret, loading, run }: TabProps) {
  const [teams, setTeams] = useState<Team[]>([])
  const [matches, setMatches] = useState<Match[]>([])
  const [assignments, setAssignments] = useState<Record<string, { home: string; away: string; dt: string; label: string }>>({})
  const [standings, setStandings] = useState<StandingsGroup[]>([])
  const [autoLabels, setAutoLabels] = useState<Record<string, { homeLabel: string; awayLabel: string }>>({})
  const [loaded, setLoaded] = useState(false)

  async function load() {
    const [ts, ms] = await Promise.all([
      run(() => getAdminTeams(secret)),
      run(() => fetchMatches() as Promise<Match[]>),
    ])
    if (ts) setTeams(ts)
    if (ms) {
      const r32 = ms.filter(m => m.round === 'R32').sort((a, b) => a.match_number - b.match_number)
      setMatches(r32)
      const init: Record<string, { home: string; away: string; dt: string; label: string }> = {}
      for (const m of r32) {
        init[m.id] = {
          home: m.home_team_id ?? '',
          away: m.away_team_id ?? '',
          dt: m.scheduled_at ? new Date(m.scheduled_at * 1000).toISOString().slice(0, 16) : '',
          label: '',
        }
      }
      setAssignments(init)
      setLoaded(true)
    }
  }

  async function handleAutoFill() {
    const result = await run(() => getStandings(secret))
    if (!result) return

    // Reload teams — getStandings upserts new teams (SWE, NOR, CPV, etc.) into the DB
    const freshTeams = await run(() => getAdminTeams(secret))
    if (freshTeams) setTeams(freshTeams)

    setStandings(result.standings)

    const newAssignments = { ...assignments }
    const newLabelMap: Record<string, { homeLabel: string; awayLabel: string }> = {}
    for (const a of result.assignments as ProposedAssignment[]) {
      if (!newAssignments[a.matchId]) continue
      newAssignments[a.matchId] = {
        ...newAssignments[a.matchId],
        home: a.homeTeamId ?? newAssignments[a.matchId].home,
        away: a.awayTeamId ?? newAssignments[a.matchId].away,
        label: `${a.homeLabel} vs ${a.awayLabel}`,
      }
      newLabelMap[a.matchId] = { homeLabel: a.homeLabel, awayLabel: a.awayLabel }
    }
    setAssignments(newAssignments)
    setAutoLabels(newLabelMap)
  }

  function set(matchId: string, field: 'home' | 'away' | 'dt', value: string) {
    setAssignments(prev => ({ ...prev, [matchId]: { ...prev[matchId], [field]: value } }))
  }

  async function handleSave() {
    const payload = Object.entries(assignments)
      .filter(([, v]) => v.home && v.away)
      .map(([matchId, v]) => ({
        matchId,
        homeTeamId: v.home,
        awayTeamId: v.away,
        scheduledAt: v.dt ? Math.floor(new Date(v.dt).getTime() / 1000) : undefined,
      }))
    const result = await run(() => assignTeams(payload, secret, false))
    if (result) {
      const msg = result.predictionsCleared > 0
        ? `Saved ${payload.length} matchups — cleared predictions for ${result.predictionsCleared} changed match(es)`
        : `Saved ${payload.length} matchups (marked as preliminary)`
      toast(msg, result.predictionsCleared > 0 ? 'info' : 'success')
    }
  }

  async function handleConfirm() {
    const payload = Object.entries(assignments)
      .filter(([, v]) => v.home && v.away)
      .map(([matchId, v]) => ({
        matchId,
        homeTeamId: v.home,
        awayTeamId: v.away,
        scheduledAt: v.dt ? Math.floor(new Date(v.dt).getTime() / 1000) : undefined,
      }))
    const result = await run(() => assignTeams(payload, secret, true))
    if (result) {
      const msg = result.predictionsCleared > 0
        ? `Confirmed ${payload.length} matchups — cleared ${result.predictionsCleared} stale prediction(s)`
        : `Confirmed ${payload.length} matchups — preliminary badge removed`
      toast(msg, 'success')
    }
  }

  const byGroup = teams.reduce<Record<string, Team[]>>((acc, t) => {
    const g = t.group_label ?? '?'
    ;(acc[g] = acc[g] ?? []).push(t)
    return acc
  }, {})
  const sortedGroups = Object.keys(byGroup).sort()

  if (!loaded) {
    return (
      <div className="card p-6 text-center">
        <p className="text-white/50 text-sm mb-4">Load team list to set R32 matchups.</p>
        <button className="btn-primary" disabled={loading || !secret} onClick={load}>
          {loading ? <Spinner size="sm" /> : null} Load
        </button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Auto-fill banner */}
      <div className="card p-4 border border-emerald-500/30 bg-emerald-900/10">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <p className="text-sm font-semibold text-emerald-300 mb-0.5">Auto-fill from live standings</p>
            <p className="text-xs text-white/40">
              Pulls current group stage standings from football-data.org and populates
              matchups based on the 2026 WC seeding table. You can still edit any slot before saving.
            </p>
          </div>
          <button className="btn-primary shrink-0" disabled={loading} onClick={handleAutoFill}>
            {loading ? <Spinner size="sm" /> : '⚡'} Auto-fill from Standings
          </button>
        </div>
      </div>

      {/* Group standings table (shown after auto-fill) */}
      {standings.length > 0 && (
        <div className="card p-4">
          <h3 className="text-xs font-bold text-white/60 uppercase tracking-wider mb-3">Current Group Standings</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">
            {standings.map(g => (
              <div key={g.group} className="bg-pitch-800 rounded-lg p-2">
                <div className="text-xs font-bold text-gold-400 mb-1">Group {g.group}</div>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-white/30">
                      <th className="text-left pb-1">#</th>
                      <th className="text-left pb-1">Team</th>
                      <th className="text-right pb-1">P</th>
                      <th className="text-right pb-1">Pts</th>
                      <th className="text-right pb-1">GD</th>
                    </tr>
                  </thead>
                  <tbody>
                    {g.table.slice(0, 4).map(row => (
                      <tr key={row.tla} className={row.position <= 2 ? 'text-white' : 'text-white/40'}>
                        <td className="py-0.5">{row.position}</td>
                        <td className="py-0.5 font-medium">{row.tla}</td>
                        <td className="text-right py-0.5">{row.played}</td>
                        <td className="text-right py-0.5 font-bold text-gold-400">{row.points}</td>
                        <td className="text-right py-0.5">{row.gd > 0 ? `+${row.gd}` : row.gd}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* R32 match slots */}
      <div className="grid gap-3">
        {matches.map(m => {
          const a = assignments[m.id] ?? { home: '', away: '', dt: '', label: '' }
          const lbl = autoLabels[m.id]
          return (
            <div key={m.id} className="card p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="text-xs font-bold text-gold-400">R32 #{m.match_number}</span>
                {lbl && (
                  <span className="text-[10px] bg-emerald-900/50 text-emerald-300 px-2 py-0.5 rounded-full">
                    {lbl.homeLabel} vs {lbl.awayLabel}
                  </span>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-white/40 mb-1 block">Home</label>
                  <select className="input text-sm" value={a.home} onChange={e => set(m.id, 'home', e.target.value)}>
                    <option value="">— select —</option>
                    {sortedGroups.map(g => (
                      <optgroup key={g} label={`Group ${g}`}>
                        {byGroup[g].map(t => (
                          <option key={t.id} value={t.id}>{t.flag_emoji ?? ''} {t.name} ({t.id})</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/40 mb-1 block">Away</label>
                  <select className="input text-sm" value={a.away} onChange={e => set(m.id, 'away', e.target.value)}>
                    <option value="">— select —</option>
                    {sortedGroups.map(g => (
                      <optgroup key={g} label={`Group ${g}`}>
                        {byGroup[g].map(t => (
                          <option key={t.id} value={t.id}>{t.flag_emoji ?? ''} {t.name} ({t.id})</option>
                        ))}
                      </optgroup>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-white/40 mb-1 block">Kickoff (local time)</label>
                  <input
                    type="datetime-local"
                    className="input text-sm"
                    value={a.dt}
                    onChange={e => set(m.id, 'dt', e.target.value)}
                  />
                </div>
              </div>
            </div>
          )
        })}
      </div>

      <div className="flex gap-3">
        <button className="btn-secondary flex-1" disabled={loading || !secret} onClick={handleSave}>
          {loading ? <Spinner size="sm" /> : '💾'} Save as Preliminary
        </button>
        <button className="btn-primary flex-1" disabled={loading || !secret} onClick={handleConfirm}>
          {loading ? <Spinner size="sm" /> : '✅'} Confirm Final Matchups
        </button>
      </div>
      <p className="text-xs text-white/30 text-center -mt-2">
        Use <strong className="text-white/50">Save as Preliminary</strong> while the group stage is ongoing.
        Use <strong className="text-white/50">Confirm</strong> once all groups are officially decided.
      </p>
    </div>
  )
}
